// import * as pdfjsLib from 'pdfjs-dist';

// pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

import pdfjsLib from '../../../common/functions/pdf_parsing.function';
// Build rows from text items with positions; find column index of "Unit Price" and value in data row below.
// Handles "Unit Price" as single cell, "Unit" + "Price" in same row, or "Unit" and "Price" on two lines (same column).
function extractUnitPriceFromTable(textContent) {
  if (!textContent?.items?.length) {
    return null;
  }
  const items = textContent.items.map((item) => {
    const x = item.transform[4] ?? 0;
    const y = item.transform[5] ?? 0;
    return { str: item.str || '', x, y };
  });

  const yTolerance = 4;
  const yToItems = {};
  items.forEach((item) => {
    const yKey = Math.round(item.y / yTolerance) * yTolerance;
    if (!yToItems[yKey]) {
      yToItems[yKey] = [];
    }
    yToItems[yKey].push(item);
  });

  const sortedYs = Object.keys(yToItems)
    .map(Number)
    .sort((a, b) => b - a);

  const rows = sortedYs.map((y) => {
    const rowItems = yToItems[y].sort((a, b) => a.x - b.x);
    return rowItems;
  });

  const xTolerance = 25;
  let unitPriceX = null;
  let firstDataRowIndex = -1;

  // 1) Same row: "Unit Price" (single cell) or "Unit" + "Price" (two cells)
  for (let r = 0; r < rows.length; r++) {
    const row = rows[r];
    const rowText = row
      .map((it) => it.str)
      .join(' ')
      .toLowerCase();
    if (!rowText.includes('unit') || !rowText.includes('price')) {
      continue;
    }

    const singleIdx = row.findIndex((it) => /unit\s*price/i.test(String(it.str).trim()));
    if (singleIdx >= 0) {
      unitPriceX = row[singleIdx]?.x ?? row[singleIdx - 1]?.x;
      firstDataRowIndex = r + 1;
      break;
    }

    const unitIdx = row.findIndex((it) => String(it.str).trim().toLowerCase() === 'unit');
    const priceIdx =
      unitIdx >= 0
        ? row.findIndex((it, i) => i >= unitIdx && String(it.str).trim().toLowerCase() === 'price')
        : -1;
    if (unitIdx >= 0 && priceIdx >= 0) {
      unitPriceX = row[priceIdx]?.x ?? row[unitIdx]?.x ?? row[priceIdx - 1]?.x;
      firstDataRowIndex = r + 1;
      break;
    }
  }

  // 2) Two lines: "Unit" on one row, "Price" on the next row (same column / same X)
  if (unitPriceX == null && firstDataRowIndex < 0) {
    for (let r = 0; r < rows.length - 1; r++) {
      const row = rows[r];
      const nextRow = rows[r + 1];
      const unitCell = row.find((it) => String(it.str).trim().toLowerCase() === 'unit');
      if (!unitCell) {
        continue;
      }
      const priceCell = nextRow.find(
        (it) =>
          String(it.str).trim().toLowerCase() === 'price' &&
          Math.abs(it.x - unitCell.x) <= xTolerance
      );
      if (priceCell) {
        unitPriceX = (unitCell.x + priceCell.x) / 2;
        firstDataRowIndex = r + 2;
        break;
      }
    }
  }

  // 3) Also try "Price" above "Unit" (reverse order in PDF)
  if (unitPriceX == null && firstDataRowIndex < 0) {
    for (let r = 1; r < rows.length; r++) {
      const row = rows[r];
      const prevRow = rows[r - 1];
      const unitCell = row.find((it) => String(it.str).trim().toLowerCase() === 'unit');
      if (!unitCell) {
        continue;
      }
      const priceCell = prevRow.find(
        (it) =>
          String(it.str).trim().toLowerCase() === 'price' &&
          Math.abs(it.x - unitCell.x) <= xTolerance
      );
      if (priceCell) {
        unitPriceX = (unitCell.x + priceCell.x) / 2;
        firstDataRowIndex = r + 1;
        break;
      }
    }
  }

  if (unitPriceX == null || firstDataRowIndex < 0) {
    return null;
  }

  for (let r = firstDataRowIndex; r < rows.length; r++) {
    const row = rows[r];
    const cell = row.find((it) => Math.abs(it.x - unitPriceX) <= xTolerance);
    if (!cell) {
      continue;
    }
    const cleaned = String(cell.str).replace(/,/g, '').trim();
    const numMatch = cleaned.match(/^[\d.]+$/);
    if (numMatch) {
      return numMatch[0];
    }
  }
  return null;
}

export const parsePDFFile = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    let fullText = '';
    let unitPriceFromTable = null;

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map((item) => item.str).join(' ');
      fullText += pageText + '\n';

      if (unitPriceFromTable == null) {
        unitPriceFromTable = extractUnitPriceFromTable(textContent);
      }
    }

    // Extract required fields using regex patterns
    const extractedData = {
      vendor: '',
      poNumber: '',
      poDate: '',
      deliverDate: '',
      poAmount: '',
      unitPrice: '',
    };

    // Capture page-1 items for positional extraction (more reliable for some PDFs like PO 6857).
    // NOTE: we keep existing regex-based logic as fallback to avoid breaking working PDFs.
    const firstPage = await pdf.getPage(1);
    const firstTextContent = await firstPage.getTextContent();
    const page1Items = (firstTextContent?.items || []).map((it) => {
      const x = it?.transform?.[4] ?? 0;
      const y = it?.transform?.[5] ?? 0;
      const str = String(it?.str || '').trim();
      return { str, x, y };
    });

    const groupRows = (items, yTolerance = 3) => {
      const rowsByY = new Map();
      items.forEach((it) => {
        if (!it.str) {
          return;
        }
        const yKey = Math.round(it.y / yTolerance) * yTolerance;
        if (!rowsByY.has(yKey)) {
          rowsByY.set(yKey, []);
        }
        rowsByY.get(yKey).push(it);
      });
      const ys = [...rowsByY.keys()].sort((a, b) => b - a);
      return ys.map((y) => rowsByY.get(y).sort((a, b) => a.x - b.x));
    };

    const page1Rows = groupRows(page1Items);

    const extractRightOfLabelInRow = (labelRe, valueRe) => {
      for (const row of page1Rows) {
        const labelIdx = row.findIndex((c) => labelRe.test(c.str));
        if (labelIdx < 0) {
          continue;
        }
        const right = row.slice(labelIdx + 1);
        for (const c of right) {
          const m = String(c.str || '').match(valueRe);
          if (m?.[0]) {
            return m[0];
          }
        }
      }
      return '';
    };

    const extractBoxTextRightOfLabel = (labelRe, stopRe) => {
      for (const row of page1Rows) {
        const labelIdx = row.findIndex((c) => labelRe.test(c.str));
        if (labelIdx < 0) {
          continue;
        }
        const labelCell = row[labelIdx];
        // Boxes vary by template; keep generous bounds around the label row.
        const xMin = labelCell.x - 5;
        const yTop = labelCell.y + 20;
        const yBottom = labelCell.y - 150;

        const boxText = page1Items
          .filter((c) => c.x >= xMin && c.y <= yTop && c.y >= yBottom)
          .sort((a, b) => b.y - a.y || a.x - b.x)
          .map((c) => c.str)
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim();

        if (!boxText) {
          continue;
        }
        const stopIdx = stopRe ? boxText.search(stopRe) : -1;
        const candidate = (stopIdx >= 0 ? boxText.slice(0, stopIdx) : boxText).trim();
        if (candidate) {
          return candidate;
        }
      }
      return '';
    };

    const extractBetween = (text, startRe, endRes = []) => {
      const t = String(text || '');
      const startIdx = t.search(startRe);
      if (startIdx < 0) {
        return '';
      }
      const after = t.slice(startIdx).replace(startRe, '');
      let endIdx = after.length;
      endRes.forEach((re) => {
        const i = after.search(re);
        if (i >= 0) {
          endIdx = Math.min(endIdx, i);
        }
      });
      return after.slice(0, endIdx).replace(/\s+/g, ' ').trim();
    };

    const extractDateTokenNear = (text, labelRe) => {
      const t = String(text || '');
      const idx = t.search(labelRe);
      if (idx < 0) {
        return '';
      }
      // Only look at a small window after the label so we never swallow the whole PDF.
      const tail = t.slice(idx, idx + 220);
      const m = tail.match(/(\d{1,2}-[A-Z]{3}-\d{2,4})/i);
      return m?.[1] ? formatDate(m[1]) : '';
    };

    // Extract Order To (Vendor) - capture only the header vendor block.
    // Some PDFs have no real newlines in extracted text, so we bound by nearby header labels.
    extractedData.vendor =
      extractBoxTextRightOfLabel(
        /Order\s*To\b/i,
        /\b(?:Contact\s*Person|On\s*Behalf\s*Of|Owner|Ship\s*To|Buyers?\s*Name)\b/i
      ) ||
      extractBetween(fullText, /Order\s*To\s*[:：]?\s*/i, [
        /On\s*Behalf\s*Of\b/i,
        /Contact\s*Person\b/i,
        /Owner\b/i,
        /Ship\s*To\b/i,
        /Buyers?\s*Name\b/i,
        /Payment\s*Terms\b/i,
      ]) ||
      '';

    // Extract PO No - keep existing behavior but add safe fallbacks for PDFs where ":"/underline splits tokens.
    // Examples:
    // - "PO No : 14073-PMCL"
    // - "PO No : 6857" (sometimes "PO No" and "6857" are separated by extra spaces/characters)
    // - Box field: "Purchase Order No: 6857"
    let poNoMatch =
      fullText.match(/Purchase Order No\s*[:：]?\s*([A-Za-z0-9]+(?:\s*-\s*[A-Za-z0-9]+)*)/i) ||
      fullText.match(/PO\s*No\s*[:：]\s*([A-Za-z0-9]+(?:\s*-\s*[A-Za-z0-9]+)*)/i) ||
      fullText.match(/PO\s*No\s*[:：]?\s*[_\-\u2013\u2014]*\s*(\d{3,}(?:\s*-\s*[A-Za-z0-9]+)*)/i) ||
      fullText.match(/PO\s*No\b[\s:：_\-\u2013\u2014]{0,20}(\d{3,}(?:\s*-\s*[A-Za-z0-9]+)*)/i) ||
      // Fallback for PDFs where the PO number is only shown as a heading without a
      // "No" label, e.g. "Purchase Order 2794". Requires a digit immediately after
      // "Purchase Order" so it never matches "Purchase Order Date" or "Purchase Order No".
      fullText.match(/Purchase\s*Order\s+(\d{3,}(?:\s*-\s*[A-Za-z0-9]+)*)/i);

    let poCandidate = poNoMatch ? String(poNoMatch[1] || '').trim() : '';

    // Fallback: some PDFs place the PO number as a separate text item (e.g. underlined),
    // so it may appear a few tokens after "PO No" in the extracted stream.
    if (!poCandidate) {
      const tokens = String(fullText || '')
        .replace(/\s+/g, ' ')
        .trim()
        .split(' ')
        .filter(Boolean);

      const norm = (s) =>
        String(s || '')
          .toLowerCase()
          .replace(/[^a-z0-9:]/g, '');
      for (let i = 0; i < tokens.length - 1; i++) {
        const a = norm(tokens[i]);
        const b = norm(tokens[i + 1]);
        const c = norm(tokens[i + 2]);

        const isPO = a === 'po' || a === 'p:o' || a === 'p.o' || a === 'p.o.' || a === 'pono';
        const isNo = b === 'no' || b === 'no:' || b === 'no.' || b === 'no:';
        const hasColon = c === ':' || c.endsWith(':');

        if (isPO && isNo) {
          // look ahead a bit for the first plausible number/token
          const start = hasColon ? i + 3 : i + 2;
          const lookahead = tokens.slice(start, start + 30).join(' ');
          const m = lookahead.match(/(\d{3,}(?:\s*-\s*[A-Za-z0-9]+)*)/);
          if (m?.[1]) {
            poCandidate = m[1];
            break;
          }
        }
      }
    }
    if (poCandidate) {
      const cleaned = poCandidate.replace(/\s+/g, '');
      // Guard against accidentally picking up non-PO header tokens.
      // Also require at least one digit; prevents captures like "Requisition" / "Department".
      if (!/^(work|order|purchase|requisition|department)$/i.test(cleaned) && /\d/.test(cleaned)) {
        extractedData.poNumber = cleaned;
      }
    }

    // Purchase Order Date: ONLY accept a real date token near the label.
    extractedData.poDate = extractRightOfLabelInRow(
      /Purchase\s*Order\s*Date\s*:?/i,
      /\d{1,2}-[A-Z]{3}-\d{2,4}/i
    )
      ? formatDate(
          extractRightOfLabelInRow(/Purchase\s*Order\s*Date\s*:?/i, /\d{1,2}-[A-Z]{3}-\d{2,4}/i)
        )
      : '' || extractDateTokenNear(fullText, /Purchase\s*Order\s*Date\s*[:：]?\s*/i) || '';

    // Delivery Date: ONLY accept a real date token near the label.
    extractedData.deliverDate = extractRightOfLabelInRow(
      /Delivery\s*Date\s*:?/i,
      /\d{1,2}-[A-Z]{3}-\d{2,4}/i
    )
      ? formatDate(extractRightOfLabelInRow(/Delivery\s*Date\s*:?/i, /\d{1,2}-[A-Z]{3}-\d{2,4}/i))
      : '' || extractDateTokenNear(fullText, /Delivery\s*Date\s*[:：]?\s*/i) || '';

    // If PO number is still empty, try the boxed "Purchase Order No" on page-1.
    if (!extractedData.poNumber) {
      const boxedPo = extractRightOfLabelInRow(
        /Purchase\s*Order\s*No\s*:?/i,
        /\d{3,}(?:-[A-Za-z0-9]+)?/i
      );
      if (boxedPo && /\d/.test(boxedPo)) {
        extractedData.poNumber = String(boxedPo).replace(/\s+/g, '').trim();
      }
    }

    // Extract Total Value - look for "Total Value" or last value in table
    const totalValueMatch =
      fullText.match(/Total Value[^\d]*([\d,]+(?:\.\d{2})?)/i) ||
      fullText.match(/Value \(PKR\)[^\d]*([\d,]+(?:\.\d{2})?)/i) ||
      fullText.match(/Total[^\d]*([\d,]+(?:\.\d{2})?)[^\d]*PKR/i);
    if (totalValueMatch) {
      // Remove commas and format
      extractedData.poAmount = totalValueMatch[1].replace(/,/g, '').trim();
    }

    // Extract Unit Price from table: find "Unit Price" column header and value in the cell directly below
    if (unitPriceFromTable) {
      extractedData.unitPrice = unitPriceFromTable;
    }

    return extractedData;
  } catch (error) {
    console.error('Error parsing PDF:', error);
    throw new Error('Failed to parse PDF file');
  }
};

// Helper function to format date
const formatDate = (dateStr) => {
  try {
    // Try to parse various date formats
    // Format: 01-AUG-2024 or 01-AUG-2024 15:16:10
    const dateMatch = dateStr.match(/(\d{1,2})-([A-Z]{3})-(\d{4})/i);
    if (dateMatch) {
      const day = dateMatch[1].padStart(2, '0');
      const month = dateMatch[2].toUpperCase();
      const year = dateMatch[3];
      return `${day}-${month}-${year}`;
    }

    // If it's already in a standard format, try to parse it
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      const months = [
        'JAN',
        'FEB',
        'MAR',
        'APR',
        'MAY',
        'JUN',
        'JUL',
        'AUG',
        'SEP',
        'OCT',
        'NOV',
        'DEC',
      ];
      const day = String(date.getDate()).padStart(2, '0');
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }

    return dateStr;
  } catch (error) {
    return dateStr;
  }
};
